import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Chip,
    Grid,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import api from '../../../../services/api';

const formatCurrencyBR = (value) => {
    const number = Number(value);
    if (Number.isNaN(number)) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(number);
};

const StatCard = ({ label, value }) => (
    <Card>
        <CardContent>
            <Typography variant="caption" color="text.secondary">
                {label}
            </Typography>
            <Typography variant="h5">{value}</Typography>
        </CardContent>
    </Card>
);

const SalesReport = () => {
    const [appId, setAppId] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [groupBy, setGroupBy] = useState('day');
    const [data, setData] = useState(null);

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        carregarRelatorio();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, groupBy]);

    const carregarRelatorio = async (customStart, customEnd) => {
        const params = new URLSearchParams();
        if (customStart || startDate) params.set('start_date', customStart || startDate);
        if (customEnd || endDate) params.set('end_date', customEnd || endDate);
        params.set('group_by', groupBy);
        const res = await api.get(`v1/apps/${appId}/reports/sales/?${params.toString()}`);
        setData(res.data);
        setStartDate(res.data.start_date);
        setEndDate(res.data.end_date);
    };

    if (!data) return null;

    return (
        <Box p={{ xs: 2, md: 4 }}>
            <Typography variant="h4" fontWeight="700" mb={1}>
                Relatório de Vendas
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={4}>
                Pedidos confirmados no período
            </Typography>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
                <TextField
                    label="De"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                />
                <TextField
                    label="Até"
                    type="date"
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                />
                <TextField select label="Agrupar por" size="small" sx={{ width: 140 }} value={groupBy} onChange={(e) => setGroupBy(e.target.value)}>
                    <MenuItem value="day">Dia</MenuItem>
                    <MenuItem value="month">Mês</MenuItem>
                </TextField>
                <Chip label="Filtrar" color="primary" onClick={() => carregarRelatorio(startDate, endDate)} sx={{ cursor: 'pointer', px: 1 }} />
            </Stack>

            <Grid container spacing={2} mb={3}>
                <Grid item xs={12} sm={6}>
                    <StatCard label="Total vendido" value={formatCurrencyBR(data.total_amount)} />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <StatCard label="Pedidos confirmados" value={data.total_count} />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight={700} mb={2}>
                                Vendas por {groupBy === 'day' ? 'dia' : 'mês'}
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Período</TableCell>
                                        <TableCell align="right">Pedidos</TableCell>
                                        <TableCell align="right">Total</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.by_period.map((row) => (
                                        <TableRow key={row.period}>
                                            <TableCell>{row.period}</TableCell>
                                            <TableCell align="right">{row.count}</TableCell>
                                            <TableCell align="right">{formatCurrencyBR(row.total)}</TableCell>
                                        </TableRow>
                                    ))}
                                    {data.by_period.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                Nenhuma venda no período
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" fontWeight={700} mb={2}>
                                Produtos mais vendidos
                            </Typography>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Produto</TableCell>
                                        <TableCell align="right">Qtd</TableCell>
                                        <TableCell align="right">Receita</TableCell>
                                        <TableCell align="right">Margem</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.top_products.map((p) => (
                                        <TableRow key={p.produto_variacao}>
                                            <TableCell>{p.nome}</TableCell>
                                            <TableCell align="right">{p.quantidade}</TableCell>
                                            <TableCell align="right">{formatCurrencyBR(p.receita)}</TableCell>
                                            <TableCell align="right" sx={{ color: 'success.main' }}>
                                                {formatCurrencyBR(p.margem)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {data.top_products.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={4} align="center">
                                                Nenhum produto vendido no período
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SalesReport;
